import { Box } from '@material-ui/core';
import { Role } from '@nemuru-eng/js-api-client';
import { createRef, LegacyRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { HorizontalScrollWrapper } from '@/components/shared/components/HorizontalScrollWrapper';
import { BreadCrumbsNav } from '@/components/views/DynamicPageView/components/BreadCrumbsNav';
import { DynamicSection } from '@/components/views/DynamicPageView/components/DynamicSection';
import { LoadingLayer } from '@/components/views/DynamicPageView/components/LoadingLayer';
import { PageRightMenu } from '@/components/views/DynamicPageView/components/PageRightMenu';
import { PageTitles } from '@/components/views/DynamicPageView/components/PageTitles';
import { Widgets } from '@/components/views/DynamicPageView/components/Widgets';
import { usePageRightMenu } from '@/components/views/DynamicPageView/hooks/usePageRightMenu';
import { NotFoundView } from '@/components/views/NotFoundView';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { SectionContextProvider } from '@/contexts/SectionContext/SectionContext';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { useSectionsObserver } from '@/hooks/useSectionsObserver';
import { useUIWrapper } from '@/hooks/useUIWrapper';

export function DynamicPageView(props: { path: string }) {
  const { path } = props;

  const {
    state: { requiredWidgets, loading, constructing, notFound, loadingDisabled, sectionsVisible },
    actions: { setPage, resetPage, addRequiredWidgets, setConstructing },
  } = usePageContext();
  const { userEntity } = useUserContext();

  const {
    state: { currentLayout },
  } = useConfigContext();

  const { appStarted } = useUserContext();
  const { showPageRightMenu } = usePageRightMenu();
  const { pageRightMenuWidth } = useUIWrapper();
  const { md } = useScreen();
  const { background } = useCustomTheme();

  const { hash } = useLocation();
  const [firstRendered, setFirstRendered] = useState(false);
  const [initialHash] = useState(hash);
  const pageInLayout = currentLayout?.pagesByPath[path];
  const blankPage = {
    titleTextId: '',
    subtitleTextId: '',
    widgets: [],
    sections: [],
  };
  const page = pageInLayout || blankPage;

  useEffect(() => {
    resetPage();
    setPage(page.sections, page.widgets);
    setFirstRendered(true);
  }, [currentLayout]);

  useEffect(() => {
    if (requiredWidgets.length) {
      addRequiredWidgets();
    } else {
      endConstructingOnNextRender();
    }
  }, [requiredWidgets]);

  useLayoutEffect(() => {
    if (!loading && !constructing && !loadingDisabled) {
      initialScrollWindow();
    }
  }, [loading, constructing]);

  const sectionStartsRefs = useRef(page?.sections.map(() => createRef()));
  const { currentSectionIndex } = useSectionsObserver(sectionStartsRefs, sectionsVisible);

  const endConstructingOnNextRender = () => {
    setTimeout(() => {
      setConstructing(false);
    }, 0);
  };

  const initialScrollWindow = () => {
    const hashTarget = hash || initialHash;
    const hashSection = document.getElementById(hashTarget.substring(1));
    window.scrollTo(0, hashSection?.getBoundingClientRect().top || 0);
  };

  const ready = !loading && !constructing && firstRendered;
  const visible = ready || loadingDisabled;
  const pageRightSpace = showPageRightMenu && md ? `${pageRightMenuWidth}px` : 0;
  const lastSectionKey = Math.max(...sectionsVisible);

  return (
    <>
      {!pageInLayout || notFound ? (
        <NotFoundView />
      ) : (
        <>
          <LoadingLayer loading={!ready} />
          <Box
            width={'100%'}
            mt={'-60px'}
            className={visible ? 'appear-anim' : ''}
            paddingRight={pageRightSpace}
            style={visible ? {} : { display: 'none', opacity: 0 }}
            boxSizing={'border-box'}
          >
            <Box
              style={{
                top: 0,
                position: 'sticky',
                whiteSpace: 'nowrap',
                zIndex: 12,
                boxShadow: `-8px 15px 25px 8px ${background}`,
                marginBottom: '30px',
              }}
            >
              <Box pt={'35px'} pb={'15px'} bgcolor={background}>
                <HorizontalScrollWrapper>
                  {!userEntity?.hasRole([Role.ROLE_IBC_TLMK_AGENT]) && <BreadCrumbsNav />}
                </HorizontalScrollWrapper>
              </Box>
            </Box>
            <PageTitles pageTitleTextId={page?.titleTextId} pageSubtitleTextId={page?.subtitleTextId} />
            {page?.sections.map((section, key) => (
              <SectionContextProvider key={key} sectionKey={key}>
                <DynamicSection
                  section={section}
                  isLastSection={key === lastSectionKey}
                  sectionStartRef={sectionStartsRefs.current[key] as LegacyRef<HTMLDivElement>}
                />
              </SectionContextProvider>
            ))}
            <Widgets notShownInRightMenuOnly={true} />
          </Box>
          {showPageRightMenu && <PageRightMenu open={appStarted} currentSectionIndex={currentSectionIndex} />}
        </>
      )}
    </>
  );
}
